import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  IconButton, 
  Tooltip, 
  Zoom,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportIcon from '@mui/icons-material/Support';
import ComputerIcon from '@mui/icons-material/Computer';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CodeIcon from '@mui/icons-material/Code';

// Styled components with animation and enhanced visuals
const OrgCard = styled(Card)(({ theme, color, active }) => ({
  backgroundColor: active ? color : theme.palette.background.paper,
  color: active ? theme.palette.getContrastText(color) : theme.palette.text.primary,
  borderRadius: '12px',
  margin: theme.spacing(1),
  textAlign: 'center',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  boxShadow: active 
    ? `0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)` 
    : `0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)`,
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: `0 15px 30px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)`,
  },
  borderLeft: `5px solid ${color}`,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: '2.5rem',
    transition: 'transform 0.3s ease',
  },
}));

const ConnectorContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(1),
}));

const ConnectorLine = styled(Box)(({ theme, direction = 'vertical', color }) => ({
  backgroundColor: color || theme.palette.divider,
  ...(direction === 'vertical' ? {
    width: '3px',
    height: theme.spacing(3),
    margin: '0 auto',
  } : {
    height: '3px',
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  }),
  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
}));

const UmbrellaView = () => {
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState('all');
  const [animate, setAnimate] = useState(false);

  // Colors for different sections
  const colors = {
    main: '#1a237e',
    core: '#0d47a1',
    coreChild: '#1565c0',
    products: '#00695c',
    productsChild: '#00796b',
    services: '#5d4037',
    servicesChild: '#6d4c41'
  };

  useEffect(() => {
    setAnimate(true);
    const timer = setTimeout(() => {
      setActiveSection('all');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSectionClick = (section) => {
    if (activeSection === section) {
      setActiveSection('all');
    } else {
      setActiveSection(section);
    }
  };

  const isActive = (section) => {
    return activeSection === 'all' || activeSection === section;
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 4 }, 
      maxWidth: '1400px', 
      margin: '0 auto',
      opacity: animate ? 1 : 0,
      transform: animate ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.8s ease, transform 0.8s ease',
    }}>
      <Typography 
        variant="h3" 
        gutterBottom 
        align="center" 
        sx={{ 
          mb: 5, 
          fontWeight: 700,
          background: 'linear-gradient(45deg, #1a237e 30%, #00695c 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
        }}
      >
        AUJ Group Organizational Structure
      </Typography>

      {/* Main Holding Company - Always active */}
      <Zoom in={animate} style={{ transitionDelay: '200ms' }}>
        <OrgCard color={colors.main} active={true} elevation={10}>
          <CardContent>
            <IconContainer>
              <BusinessIcon color="inherit" fontSize="large" />
            </IconContainer>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>AUJ Group</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>(Holding Company)</Typography>
          </CardContent>
        </OrgCard>
      </Zoom>

      <ConnectorContainer>
        <ConnectorLine color={colors.main} />
      </ConnectorContainer>

      {/* Main Divisions Grid */}
      <Grid container spacing={3} justifyContent="center">
        
        {/* CORE OPERATIONS */}
        <Grid item xs={12} md={4} sx={{ 
          transition: 'all 0.5s ease',
          transform: activeSection !== 'all' && activeSection !== 'core' ? 'scale(0.95)' : 'scale(1)'
        }}>
          <Zoom in={animate} style={{ transitionDelay: '300ms' }}>
            <OrgCard 
              color={colors.core} 
              active={isActive('core')}
              onClick={() => handleSectionClick('core')}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <IconContainer>
                  <SettingsIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>CORE OPERATIONS</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(Run the company)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
          
          <ConnectorContainer>
            <ConnectorLine color={isActive('core') ? colors.core : undefined} />
          </ConnectorContainer>
          
          <Zoom in={animate} style={{ transitionDelay: '400ms' }}>
            <OrgCard color={colors.coreChild} active={isActive('core')}>
              <CardContent>
                <IconContainer>
                  <ComputerIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Smart Staff Management</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(brain of the business)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
          
          <ConnectorContainer>
            <ConnectorLine color={isActive('core') ? colors.coreChild : undefined} />
          </ConnectorContainer>
          
          <Zoom in={animate} style={{ transitionDelay: '450ms' }}>
            <OrgCard color={colors.coreChild} active={isActive('core')}>
              <CardContent>
                <IconContainer>
                  <WorkIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Career Portal</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(staffing, connected to ERP)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
          
          <ConnectorContainer>
            <ConnectorLine color={isActive('core') ? colors.coreChild : undefined} />
          </ConnectorContainer>
          
          <Zoom in={animate} style={{ transitionDelay: '500ms' }}>
            <OrgCard color={colors.coreChild} active={isActive('core')}>
              <CardContent>
                <IconContainer>
                  <AccountBalanceIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Investor Portal</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(funding, connected to ERP)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
        </Grid>

        {/* PRODUCTS */}
        <Grid item xs={12} md={4} sx={{ 
          transition: 'all 0.5s ease',
          transform: activeSection !== 'all' && activeSection !== 'products' ? 'scale(0.95)' : 'scale(1)'
        }}>
          <Zoom in={animate} style={{ transitionDelay: '550ms' }}>
            <OrgCard 
              color={colors.products} 
              active={isActive('products')}
              onClick={() => handleSectionClick('products')}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <IconContainer>
                  <ShoppingCartIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>PRODUCTS</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(Revenue generation)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
          
          <ConnectorContainer>
            <ConnectorLine color={isActive('products') ? colors.products : undefined} />
          </ConnectorContainer>
          
          <Zoom in={animate} style={{ transitionDelay: '600ms' }}>
            <OrgCard color={colors.productsChild} active={isActive('products')}>
              <CardContent>
                <IconContainer>
                  <ComputerIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Smart Staff Management</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(SaaS to external clients)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
          
          <ConnectorContainer>
            <ConnectorLine color={isActive('products') ? colors.productsChild : undefined} />
          </ConnectorContainer>
          
          <Zoom in={animate} style={{ transitionDelay: '650ms' }}>
            <OrgCard color={colors.productsChild} active={isActive('products')}>
              <CardContent>
                <IconContainer>
                  <ApartmentIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Oruland</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(Real estate platform)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
        </Grid>

        {/* SHARED SERVICES */}
        <Grid item xs={12} md={4} sx={{ 
          transition: 'all 0.5s ease',
          transform: activeSection !== 'all' && activeSection !== 'services' ? 'scale(0.95)' : 'scale(1)'
        }}>
          <Zoom in={animate} style={{ transitionDelay: '700ms' }}>
            <OrgCard 
              color={colors.services} 
              active={isActive('services')}
              onClick={() => handleSectionClick('services')}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <IconContainer>
                  <SupportIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>SHARED SERVICES</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
          
          <ConnectorContainer>
            <ConnectorLine color={isActive('services') ? colors.services : undefined} />
          </ConnectorContainer>
          
          <Zoom in={animate} style={{ transitionDelay: '750ms' }}>
            <OrgCard color={colors.servicesChild} active={isActive('services')}>
              <CardContent>
                <IconContainer>
                  <CodeIcon color="inherit" fontSize="large" />
                </IconContainer>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>AUJ Technologies</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>(Internal tech + external IT clients)</Typography>
              </CardContent>
            </OrgCard>
          </Zoom>
        </Grid>
      </Grid>

      {/* Executive Dashboard Information */}
      <Box 
        sx={{ 
          mt: 6, 
          textAlign: 'center',
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
          opacity: animate ? 1 : 0,
          transform: animate ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
          transitionDelay: '800ms',
        }}
      >
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 700, 
            color: theme.palette.primary.main,
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 1,
            mb: 2
          }}
        >
          Executive Overview Dashboard
        </Typography>
        <Typography variant="body1" paragraph sx={{ maxWidth: '800px', mx: 'auto' }}>
          This interactive view provides a high-level organizational structure of the AUJ Group holding company
          and its divisions. Each business unit is interconnected through our integrated ERP system.
          <br /><br />
          <b>Click on any major division</b> to highlight its structure and related components.
        </Typography>
      </Box>
    </Box>
  );
};

export default UmbrellaView;